import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CARP_STEPS,
  DDZ_TIERS,
  FC_CELLS,
  HB_ROOMS,
  MJ_EXCHANGES,
  MJ_HAND,
  MJT_STAKES,
  LEVER_IDS,
  SLOT_IDS,
  PUZZLE_IDS,
  STRATEGY_IDS,
  isLeverId,
  isReleased,
  isSkillGame,
  type SkillGameId,
  type GameId,
  type HongbaoPhase,
  type HongbaoRoom,
  type LobbyLiveResponse,
  type DdzLobbyTier,
  type DdzTier,
  type MjtQueueView,
  type MjtStake,
  type RoulettePhase,
  type SlotId,
} from '@tg/shared';
import { HONGBAO_CONFIG } from '@tg/games/hongbao';
import { DDZ_CONFIG } from '@tg/games/doudizhu';
import type { TickerItem } from '@/api/endpoints';
import { useLocale } from '@/i18n/useLocale';
import { useFormat } from '@/i18n/useFormat';
import { formatCountdown, multiplierColor } from '@/i18n/format';
import { paths } from '@/app/paths';
import { hasGamePage } from '@/app/soon';
import { GameCover } from '@/art/GameCover';
import type { CoverId } from '@/art/covers';
import { rasterBg } from '@/art/raster';
import { UiIcon } from '@/art/UiIcon';
import { useResolvedTheme } from '@/theme/theme';
import { buttonClasses } from '@/ui/Button';
import { cx } from '@/ui/cx';
import { useSession } from '@/stores/session';
import { Table, type Column } from '@/ui';
import {
  Logo,
  NavBadge,
  useFriendsBadge,
  useGamesBadge,
  useMessagesBadge,
  useTourneysBadge,
} from '@/shell/parts';
import { useDailyBonus } from '@/features/bonus/useDailyBonus';
import { EnvelopeArt } from '@/features/bonus/Envelope';
import bs from '@/features/bonus/bonus.module.css';
import { useCulture } from '@/i18n/culture';
import { LiftoffCurve, flightAt, type FlightState } from './LiftoffCurve';
import { useHouseBotName, useNickname } from '@/i18n/useNickname';
import { potBotSeed } from '@/i18n/botNames';
import { useNow } from './useLobbyLive';
import { NewMark } from '@/features/passport/marks';
import { sendSkillEvent } from '@/skill/beacon';
import s from './lobby.module.css';

/** Proper names stay untranslated; Roulette is translated (common.game.roulette). */
export function useGameName(): (g: GameId) => string {
  const intl = useIntl();
  return (g) => intl.formatMessage({ id: `common.game.${g}` });
}

/**
 * Extra names the lobby search matches besides the localized one: Dou Dizhu is known by its Chinese name and its
 * romanization in every language («斗地主» finds it in en, «Dou Dizhu» in zh, «Доу» in de).
 */
const SEARCH_ALIASES: Partial<Record<GameId, readonly string[]>> = {
  doudizhu: ['Dou Dizhu', 'Doudizhu', '斗地主', 'Доу Ди Чжу'],
};

/** What the lobby search matches for a game: its localized name plus the aliases above. */
export function useSearchName(): (g: GameId) => string {
  const name = useGameName();
  return (g) => [name(g), ...(SEARCH_ALIASES[g] ?? [])].join(' ');
}

/** Cards per hand in Golden Flower (lobby text). */
const ZJH_HAND_SIZE = 3;

/** Single-player originals shown as a card (desktop), a left-nav item and a phone row; no live data. */
export const SOLO_ORIGINALS = ['carp', 'firecrackers', 'mahjong', 'zhajinhua'] as const;
export type SoloOriginal = (typeof SOLO_ORIGINALS)[number];

/**
 * Originals drawn as a card with a cover: the single-player ones, Red Envelopes (shared tables: static texts plus,
 * when the server sends `hongbao`, players per table and the running countdown — see `hongbaoTables`) and Mahjong
 * Tables and Dou Dizhu (shared tables: static texts plus, when the server sends `mahjongTable` / `doudizhu`, people
 * queued per stake / tier — see `mahjongQueues`, `doudizhuQueues`).
 */
export type CoverOriginal = SoloOriginal | TableOriginal;

/** Shared tables with live seats / queues: the «Tables» section of the lobby (after the originals, before the slots). */
export const TABLE_ORIGINALS = ['hongbao', 'mahjong-table', 'doudizhu'] as const;
export type TableOriginal = (typeof TABLE_ORIGINALS)[number];

/**
 * Slots with reels («Slots») and the lever machines — one-armed bandits on the same engine (`LEVER_IDS` of
 * @tg/shared) — in their own «Machines» section after the slots; a new machine lands there by its id alone.
 */
export const REEL_SLOTS = SLOT_IDS.filter((g) => !isLeverId(g));
export const MACHINES = LEVER_IDS;

/** Skill games (@tg/shared catalog.ts): «Puzzles» (prize-pool contests + training) and «Strategy» (chess). */
export const PUZZLES = PUZZLE_IDS;
export const STRATEGY = STRATEGY_IDS;

/** Can the card lead into the game? A game behind `comingSoon` (GAME_CATALOG) or with a stub route stays disabled. */
export const isPlayable = (g: GameId): boolean => isReleased(g) && hasGamePage(g);

/** Red Envelopes tables as one label: "18 · 88 · 888" (desktop / phone texts), "18/88/888" (left nav). */
export const HB_TABLES = HB_ROOMS.join(' · ');
export const HB_TABLES_SHORT = HB_ROOMS.join('/');

/** Mahjong Tables stakes as one label, like Red Envelopes; every table has four seats, bots take the empty ones. */
export const MJT_TABLES = MJT_STAKES.join(' · ');
export const MJT_TABLES_SHORT = MJT_STAKES.join('/');
export const MJT_TABLE_SEATS = 4;

/** Dou Dizhu tiers by their base (底分) as one label, like the stakes above; three seats, bots take the empty ones. */
export const DDZ_BASES = DDZ_CONFIG.tiers.map((t) => t.base).join(' · ');
export const DDZ_BASES_SHORT = DDZ_CONFIG.tiers.map((t) => t.base).join('/');
export const DDZ_TABLE_SEATS = 3;

/** Stakes / bases of the shared tables as the short nav value. */
const TABLES_SHORT: Record<TableOriginal, string> = {
  hongbao: HB_TABLES_SHORT,
  'mahjong-table': MJT_TABLES_SHORT,
  doudizhu: DDZ_BASES_SHORT,
};

export const MECH: Record<SlotId | CoverOriginal | SkillGameId, string> = {
  garden: 'lobby.mech.lines',
  deep: 'lobby.mech.cascades',
  spaceport: 'lobby.mech.ways',
  'sweet-shop': 'lobby.mech.holdWin',
  workshop: 'lobby.mech.progress',
  'koi-pond': 'lobby.mech.multipliers',
  'lucky-lever': 'lobby.mech.lever',
  'neon-arcade': 'lobby.mech.lever',
  'pirate-cove': 'lobby.mech.lever',
  carp: 'lobby.mech.carp',
  firecrackers: 'lobby.mech.firecrackers',
  mahjong: 'lobby.mech.mahjong',
  zhajinhua: 'lobby.mech.zhajinhua',
  hongbao: 'lobby.mech.hongbao',
  'mahjong-table': 'lobby.mech.mahjongTable',
  doudizhu: 'lobby.mech.doudizhu',
  'tile-match': 'lobby.mech.pairs',
  'color-sort': 'lobby.mech.sort',
  'daily-words': 'lobby.mech.words',
  chess: 'lobby.mech.chess',
};

/** Lead of the desktop card and the size shown in the phone row: message id + ICU values. */
const ORIGINAL_TEXT: Record<
  CoverOriginal | SkillGameId,
  { lead: string; size: string; values: Record<string, number | string> }
> = {
  carp: { lead: 'lobby.carp.lead', size: 'lobby.carp.steps', values: { steps: CARP_STEPS } },
  firecrackers: {
    lead: 'lobby.firecrackers.lead',
    size: 'lobby.firecrackers.cells',
    values: { cells: FC_CELLS },
  },
  mahjong: {
    lead: 'lobby.mahjong.lead',
    size: 'lobby.mahjong.size',
    values: { tiles: MJ_HAND, exchanges: MJ_EXCHANGES },
  },
  zhajinhua: { lead: 'lobby.zhajinhua.lead', size: 'lobby.zhajinhua.size', values: { cards: ZJH_HAND_SIZE } },
  hongbao: {
    lead: 'lobby.hongbao.lead',
    size: 'lobby.hongbao.size',
    values: { tables: HB_TABLES, players: HONGBAO_CONFIG.maxPlayers },
  },
  'mahjong-table': {
    lead: 'lobby.mahjongTable.lead',
    size: 'lobby.mahjongTable.size',
    values: { tables: MJT_TABLES, seats: MJT_TABLE_SEATS },
  },
  doudizhu: {
    lead: 'lobby.doudizhu.lead',
    size: 'lobby.doudizhu.size',
    values: { bases: DDZ_BASES_SHORT, seats: DDZ_TABLE_SEATS, cap: DDZ_CONFIG.capX },
  },
  'tile-match': { lead: 'lobby.tileMatch.lead', size: 'lobby.tileMatch.size', values: {} },
  'color-sort': { lead: 'lobby.colorSort.lead', size: 'lobby.colorSort.size', values: {} },
  'daily-words': { lead: 'lobby.dailyWords.lead', size: 'lobby.dailyWords.size', values: {} },
  chess: { lead: 'lobby.chess.lead', size: 'lobby.chess.size', values: {} },
};

/** Title of one queue chip (desktop) / of the queues line (phone): «Table 18: 3 waiting, 2 playing», «Base 5: …». */
const QUEUE_TITLE: Partial<Record<CoverOriginal, string>> = {
  'mahjong-table': 'lobby.mahjongTable.queue',
  doudizhu: 'lobby.doudizhu.queue',
};

/**
 * Red Envelopes stake in the lobby: its tables (`count`, 1 from older servers), seats taken now over them and ms to
 * the start of the focus table (server clock; null — no countdown).
 */
export interface HbLiveTable {
  room: HongbaoRoom;
  phase: HongbaoPhase;
  players: number;
  capacity: number;
  leftMs: number | null;
  count: number;
}

/** Live Red Envelopes tables in table order (18, 88, 888); null without data (the card stays static). */
export function hongbaoTables(data: LiveData, serverNow: number): HbLiveTable[] | null {
  const tables = data?.hongbao?.tables;
  if (!tables || tables.length === 0) return null;
  return HB_ROOMS.flatMap((room) => {
    const t = tables.find((x) => x.room === room);
    if (!t) return [];
    const leftMs =
      t.phase === 'open' && t.countdownEndsAt !== null ? Math.max(0, t.countdownEndsAt - serverNow) : null;
    const count = typeof t.count === 'number' && Number.isInteger(t.count) && t.count > 1 ? t.count : 1;
    return [{ room, phase: t.phase, players: t.players, capacity: t.capacity, leftMs, count }];
  });
}

/** Mahjong Tables queue of one stake in the lobby: people waiting for a seat and tables playing now. */
export interface MjtLiveQueue {
  stake: MjtStake;
  queued: number;
  tables: number;
}

/**
 * Optional `mahjongTable` block of GET /api/lobby/live (`{ stakes: MjtQueueView[] }`, like GET /api/mahjong-table/lobby).
 * The field may be missing from the contract and from older servers — it is read defensively.
 */
export interface MjtLobbyLive {
  stakes: Array<Pick<MjtQueueView, 'stake' | 'queued'> & Partial<Pick<MjtQueueView, 'tables'>>>;
}

const count = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);

/** Live Mahjong Tables queues in stake order (18, 88, 888); null without data (the card stays static). */
export function mahjongQueues(data: LiveData): MjtLiveQueue[] | null {
  const block = (data as { mahjongTable?: MjtLobbyLive | null } | null)?.mahjongTable;
  const stakes = Array.isArray(block?.stakes) ? block.stakes : null;
  if (!stakes || stakes.length === 0) return null;
  const out = MJT_STAKES.flatMap((stake) => {
    const q = stakes.find((x) => x?.stake === stake);
    return q ? [{ stake, queued: count(q.queued), tables: count(q.tables) }] : [];
  });
  return out.length > 0 ? out : null;
}

/** Dou Dizhu queue of one tier in the lobby: `stake` is the tier's base (底分), shown like the Mahjong Tables stake. */
export interface DdzLiveQueue {
  tier: DdzTier;
  stake: number;
  queued: number;
  tables: number;
}

/**
 * Optional `doudizhu` block of GET /api/lobby/live (DdzLobbyResponse, like GET /api/doudizhu/lobby): live queues in
 * tier order (novice … senior); null without data (the card stays static). Read defensively like `mahjongTable`;
 * a missing or bad base falls back to the tier's base from the game config.
 */
export function doudizhuQueues(data: LiveData): DdzLiveQueue[] | null {
  const tiers = (data as { doudizhu?: { tiers?: unknown } | null } | null)?.doudizhu?.tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const list = tiers as Array<Partial<DdzLobbyTier> | null>;
  const out = DDZ_TIERS.flatMap((tier) => {
    const q = list.find((x) => x?.tier === tier);
    if (!q) return [];
    const base = count(q.base) > 0 ? count(q.base) : (DDZ_CONFIG.tiers.find((t) => t.id === tier)?.base ?? 0);
    return [{ tier, stake: base, queued: count(q.queued), tables: count(q.tables) }];
  });
  return out.length > 0 ? out : null;
}

/** Pot sector colours in join order (design: teal, orange, blue, red, grey, …). */
export const POT_COLORS = [
  'var(--teal)',
  'var(--orange)',
  'var(--blue)',
  'var(--red)',
  'var(--muted)',
  'var(--gold)',
  'var(--purple)',
  'var(--green)',
  'var(--pink)',
];

/** conic-gradient for the pot donut: sectors ∝ stake, thin panel-coloured gaps, rest of the bank grey. */
export function potGradient(stakes: number[], bank: number, gap = 0.8): string {
  const total = Math.max(
    bank,
    stakes.reduce((a, b) => a + b, 0),
    1,
  );
  let acc = 0;
  const parts: string[] = [];
  stakes.forEach((st, i) => {
    const a0 = (acc / total) * 100;
    acc += st;
    const a1 = (acc / total) * 100;
    parts.push(
      `${POT_COLORS[i % POT_COLORS.length]} ${a0.toFixed(2)}% ${Math.max(a0, a1 - gap).toFixed(2)}%`,
    );
    parts.push(`var(--panel) ${Math.max(a0, a1 - gap).toFixed(2)}% ${a1.toFixed(2)}%`);
  });
  const rest = (acc / total) * 100;
  if (rest < 100)
    parts.push(
      `var(--border-2) ${rest.toFixed(2)}% ${(100 - gap).toFixed(2)}%`,
      `var(--panel) ${(100 - gap).toFixed(2)}% 100%`,
    );
  return `conic-gradient(${parts.join(',')})`;
}

/** Shown instead of a number when the server has no data for it (offline, no round yet). */
export const DASH = '—';

/** Live data or null (neutral state). */
export type LiveData = LobbyLiveResponse | null;

/** Roulette phase + ms left in it (server clock); null = no round. */
export type RouletteClock = { phase: RoulettePhase; leftMs: number } | null;

/** "T-00:11" while taking bets, "Rolling" during the spin / result, "—" without a round. */
export function useRouletteLabel(): (r: RouletteClock, prefix?: boolean) => string {
  const intl = useIntl();
  return (r, prefix = true) => {
    if (!r) return DASH;
    if (r.phase !== 'bets') return intl.formatMessage({ id: 'lobby.roulette.rolling' });
    return `${prefix ? 'T-' : ''}${formatCountdown(r.leftMs)}`;
  };
}

/** Slot "playing" counter: distinct players with a spin in the last N minutes (title explains it); "—" without data. */
export function SlotPlayers({
  data,
  game,
  style,
  className,
}: {
  data: LiveData;
  game: SlotId;
  style?: CSSProperties;
  className?: string;
}) {
  const f = useFormat();
  const intl = useIntl();
  const n = data?.slots[game];
  if (n === undefined || !data)
    return (
      <span className={className} style={style}>
        {DASH}
      </span>
    );
  return (
    <span
      className={className}
      style={style}
      title={intl.formatMessage({ id: 'lobby.slotPlayers' }, { count: n, min: data.activeWindowMin })}
    >
      {f.int(n)}
    </span>
  );
}

/** "12/32" pot fill, "—" without a round. */
export const potFill = (data: LiveData): string =>
  data?.pot ? `${data.pot.players}/${data.pot.capacity}` : DASH;

/** Share of the round's stakes per colour; null when nothing is staked yet. */
export function rouletteShares(data: LiveData): { red: number; black: number; green: number } | null {
  const st = data?.roulette?.staked;
  if (!st) return null;
  const total = st.red + st.black + st.green;
  return total > 0 ? { red: st.red / total, black: st.black / total, green: st.green / total } : null;
}

/* ---------------- Left nav ---------------- */
/** Nav item: a small cover, the name and a live / short value (a menu of pictures, not a text list). */
function NavGame({ game, to, children }: { game: GameId; to: string; children: ReactNode }) {
  const name = useGameName();
  if (!isPlayable(game))
    return (
      <span className={cx(s.navItem, s.navSoon)} aria-disabled="true" data-game={game}>
        <GameCover game={game} width={32} height={22} className={s.navThumb} lazy />
        <span className={s.navName}>{name(game)}</span>
        {children}
      </span>
    );
  return (
    <Link to={to} className={s.navItem}>
      <GameCover game={game} width={32} height={22} className={s.navThumb} lazy />
      <span className={s.navName}>{name(game)}</span>
      <NewMark game={game} variant="dot" />
      {children}
    </Link>
  );
}

/**
 * The left menu's main items carry the raster icons of the phone tab bar as one set (games / friends / messages
 * through their small editions): all four or none — without them the items stay text, as before.
 */
const NAV_ICONS = ['games', 'tournaments', 'friends', 'messages'] as const;
const NAV_ICON_PX = 18;
const navIcon = (name: (typeof NAV_ICONS)[number]) => (
  <UiIcon name={name} size={NAV_ICON_PX} together={NAV_ICONS} fallback={null} className={s.navIco} />
);
/** the «Machines» caption: the machine icon's small edition, nothing without the raster */
const NAV_CAP_ICON_PX = 16;

export function LeftNav({
  data,
  flight,
  roulette,
  filter,
}: {
  data: LiveData;
  flight: FlightState;
  roulette: RouletteClock;
  filter: (name: string) => boolean;
}) {
  const l = useLocale();
  const f = useFormat();
  const name = useGameName();
  const intl = useIntl();
  const rouletteLabel = useRouletteLabel();
  const games = useGamesBadge();
  const tourneys = useTourneysBadge();
  const friends = useFriendsBadge();
  const messages = useMessagesBadge();
  const searchName = useSearchName();
  const tables = TABLE_ORIGINALS.filter((g) => filter(searchName(g)));
  const machines = MACHINES.filter((g) => filter(name(g)));
  const puzzles = PUZZLES.filter((g) => filter(searchName(g)));
  const strategy = STRATEGY.filter((g) => filter(searchName(g)));
  return (
    <aside className={s.nav}>
      <div className={s.navLogo}>
        <Logo />
      </div>
      <nav className={s.navMain} aria-label={intl.formatMessage({ id: 'common.nav.label' })}>
        <NavLink to={paths.lobby(l)} end aria-label={games.label}>
          {navIcon('games')}
          <FormattedMessage id="common.nav.games" />
          <NavBadge count={games.count} dot={games.dot} />
        </NavLink>
        <NavLink to={paths.tournaments(l)} aria-label={tourneys.label}>
          {navIcon('tournaments')}
          <FormattedMessage id="common.nav.tournaments" />
          <NavBadge count={tourneys.count} />
        </NavLink>
        <NavLink to={paths.friends(l)} aria-label={friends.label}>
          {navIcon('friends')}
          <FormattedMessage id="common.nav.friends" />
          <NavBadge count={friends.count} />
        </NavLink>
        <NavLink to={paths.messages(l)} aria-label={messages.label}>
          {navIcon('messages')}
          <FormattedMessage id="common.nav.messages" />
          <NavBadge count={messages.count} />
        </NavLink>
      </nav>
      <div className={s.navGames}>
        <div className={s.navCap}>
          <FormattedMessage id="lobby.originals" />
        </div>
        {filter(name('crash')) && (
          <NavGame game="crash" to={paths.game(l, 'crash')}>
            <span className={s.navVal} style={{ color: flight.crashed ? 'var(--down)' : 'var(--up)' }}>
              {flight.m === null ? DASH : f.mult(flight.m)}
            </span>
          </NavGame>
        )}
        {filter(name('pot')) && (
          <NavGame game="pot" to={paths.game(l, 'pot')}>
            <span className={s.navVal} style={{ color: 'var(--muted)' }}>
              {potFill(data)}
            </span>
          </NavGame>
        )}
        {filter(name('roulette')) && (
          <NavGame game="roulette" to={paths.game(l, 'roulette')}>
            <span className={cx(s.navVal, s.countdown)}>{rouletteLabel(roulette, false)}</span>
          </NavGame>
        )}
        {SOLO_ORIGINALS.filter((g) => filter(name(g))).map((g) => (
          <NavGame key={g} game={g} to={paths.game(l, g)}>
            <span className={s.navVal} style={{ color: 'var(--dim)' }}>
              <FormattedMessage id={MECH[g]} />
            </span>
          </NavGame>
        ))}
        {(
          [
            ['lobby.puzzles', puzzles],
            ['lobby.strategy', strategy],
          ] as const
        ).map(
          ([cap, list]) =>
            list.length > 0 && (
              <div key={cap} className={s.navGroup}>
                <div className={`${s.navCap} ${s.navCap2}`}>
                  <FormattedMessage id={cap} />
                </div>
                {list.map((g) => (
                  <NavGame key={g} game={g} to={paths.game(l, g)}>
                    <span className={s.navVal} style={{ color: 'var(--dim)' }}>
                      <FormattedMessage id={isPlayable(g) ? MECH[g] : 'common.soon.title'} />
                    </span>
                  </NavGame>
                ))}
              </div>
            ),
        )}
        {tables.length > 0 && (
          <div className={`${s.navCap} ${s.navCap2}`}>
            <FormattedMessage id="lobby.tablesCap" />
          </div>
        )}
        {tables.map((g) => (
          <NavGame key={g} game={g} to={paths.game(l, g)}>
            <span className={s.navVal} style={{ color: 'var(--dim)' }}>
              {TABLES_SHORT[g]}
            </span>
          </NavGame>
        ))}
        <div className={`${s.navCap} ${s.navCap2}`}>
          <FormattedMessage id="lobby.slotsCap" />
        </div>
        {REEL_SLOTS.filter((g) => filter(name(g))).map((g) => (
          <NavGame key={g} game={g} to={paths.slot(l, g)}>
            <SlotPlayers data={data} game={g} className={s.navVal} style={{ color: 'var(--dim)' }} />
          </NavGame>
        ))}
        {machines.length > 0 && (
          <div className={`${s.navCap} ${s.navCap2}`}>
            <UiIcon name="machines" size={NAV_CAP_ICON_PX} fallback={null} className={s.navCapIco} />
            <FormattedMessage id="lobby.machinesCap" />
          </div>
        )}
        {machines.map((g) => (
          <NavGame key={g} game={g} to={paths.slot(l, g)}>
            <SlotPlayers data={data} game={g} className={s.navVal} style={{ color: 'var(--dim)' }} />
          </NavGame>
        ))}
      </div>
      <div className={s.navFill} />
      <DailyWidget />
    </aside>
  );
}

/* ---------------- Daily widget (left nav bottom) ---------------- */
export function DailyWidget() {
  const st = useDailyBonus((x) => x.state);
  const open = useSession((x) => x.openDaily);
  const f = useFormat();
  const cn = useCulture() === 'cn';
  // server: `day` is the claimable day, or the day already claimed today
  const filled = st.day;
  const reward = st.rewards[st.day - 1] ?? 0;
  return (
    <div className={s.daily}>
      <div className={s.dailyHead}>
        <span>
          <FormattedMessage id="common.dailyBonus" />
        </span>
        <span className="mono" style={{ color: 'var(--orange-text)' }}>
          {st.day}/7
        </span>
      </div>
      {cn ? (
        <div className={bs.miniEnv} aria-hidden="true">
          <EnvelopeArt state={st.claimableNow ? 'sealed' : 'open'} width={24} />
          <FormattedMessage id="bonus.envelope.widget" />
        </div>
      ) : (
        <div className={s.dailyBars} aria-hidden="true">
          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
            <span key={d} data-on={d <= filled} />
          ))}
        </div>
      )}
      <button type="button" className={s.dailyClaim} onClick={() => open(true)} disabled={!st.claimableNow}>
        {st.claimableNow ? (
          <>
            <FormattedMessage id="common.claim" /> <span className="mono">+{f.int(reward)}</span>
          </>
        ) : (
          <FormattedMessage id="bonus.claimed" />
        )}
      </button>
    </div>
  );
}

/* ---------------- Ticker ---------------- */
function tickerDetail(
  t: TickerItem,
  f: ReturnType<typeof useFormat>,
  short: (c: 'red' | 'black' | 'green') => string,
): string {
  if (t.multiplier) return f.mult(t.multiplier);
  if (t.color) return short(t.color);
  return '';
}

/** `items` null: no server data — only a dash (not "no big wins yet", which would be a claim). */
export function Ticker({ items, mobile }: { items: TickerItem[] | null; mobile?: boolean }) {
  const f = useFormat();
  const nick = useNickname();
  const intl = useIntl();
  const name = useGameName();
  const short = (c: 'red' | 'black' | 'green') => intl.formatMessage({ id: `lobby.roulette.short.${c}` });
  const row = (key: string, hidden?: boolean) =>
    (items ?? []).map((t, i) => {
      const detail = tickerDetail(t, f, short);
      return (
        <span key={`${key}${i}`} className={s.tickerItem} aria-hidden={hidden || undefined}>
          <span>{nick(t.nickname)}</span>
          <span style={{ color: t.amount >= 10000 ? 'var(--orange-text)' : 'var(--win)' }}>
            {f.tokens(t.amount, { sign: true })}
          </span>
          {!mobile && <span>{name(t.game)}</span>}
          {detail && <span>{detail}</span>}
        </span>
      );
    });
  return (
    <div className={mobile ? s.mTicker : s.ticker} aria-label={intl.formatMessage({ id: 'lobby.liveWins' })}>
      <span className={s.tickerLive}>
        ● <FormattedMessage id="lobby.live" />
      </span>
      {items === null ? (
        <span>{DASH}</span>
      ) : items.length === 0 ? (
        <span>
          <FormattedMessage id="lobby.tickerEmpty" />
        </span>
      ) : (
        <div className={s.tickerViewport}>
          <div
            className={s.tickerTrack}
            style={
              { ['--ticker-dur' as string]: `${Math.max(20, (items?.length ?? 0) * 7)}s` } as CSSProperties
            }
          >
            {row('a')}
            {row('b', true)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Scene behind the live widgets ---------------- */
type SceneGame = 'crash' | 'pot' | 'roulette';
const SCENE_COVER: Record<SceneGame, CoverId> = { crash: 'crash-hero', pot: 'pot', roulette: 'roulette' };
/** Width of the 1x scene file (16:9, 1280×720); the @2x file only where a widget needs more. */
const SCENE_W = 1280;

/**
 * The game's scene (raster, per theme) under a live widget, toned down by a panel-coloured veil so the numbers
 * stay readable; without the raster — the game's vector cover. Decorative.
 */
export function SceneBg({ game, width, height }: { game: SceneGame; width: number; height: number }) {
  const theme = useResolvedTheme();
  const pick = (w: number) => rasterBg(game, 'scene', theme, w > SCENE_W ? 2 : 1);
  const x1 = pick(width);
  return (
    <div className={s.scene} data-scene={game} aria-hidden="true">
      {x1 ? (
        <img
          src={x1}
          srcSet={`${x1} 1x, ${pick(width * 2)} 2x`}
          width={width}
          height={height}
          alt=""
          draggable={false}
        />
      ) : (
        <GameCover game={SCENE_COVER[game]} width={width} height={height} />
      )}
    </div>
  );
}

/* ---------------- Liftoff hero ---------------- */
/** Round number and counters; counters null without data or while they belong to the previous round. */
export function crashCounters(data: LiveData, stale: boolean) {
  const c = data?.crash ?? null;
  return { roundNo: `#${c ? c.roundNo : DASH}`, counters: c && !stale ? c : null };
}

/** Under the multiplier: "waiting for a round", "bets close in 00:05" or the flight time. */
export function FlightCaption({ flight }: { flight: FlightState }) {
  if (flight.phase === null) return <FormattedMessage id="lobby.waiting" />;
  if (flight.phase === 'betting')
    return <FormattedMessage id="lobby.crash.betting" values={{ time: formatCountdown(flight.leftMs) }} />;
  return <>T+00:{(flight.tMs / 1000).toFixed(1).padStart(4, '0')}</>;
}

/** Keep rapid flight updates inside the chart; the catalogue and its images only tick once a second. */
export function LiftoffChart({
  data,
  offset,
  compact = false,
}: {
  data: LiveData;
  offset: number;
  compact?: boolean;
}) {
  const f = useFormat();
  const now = useNow(1000, data?.crash?.phase === 'flying');
  const flight = flightAt(data?.crash ?? null, now + offset);
  const col = flight.m === null ? 'var(--dim)' : flight.crashed ? 'var(--down)' : 'var(--up)';
  return (
    <div className={compact ? s.mChart : s.chart}>
      <LiftoffCurve tMs={flight.tMs} crashed={flight.crashed} compact={compact} />
      <div className={compact ? s.mMult : s.bigMult} style={{ color: col }} aria-live="off">
        {flight.m === null ? DASH : f.mult(flight.m)}
      </div>
      {!compact && (
        <div className={s.tPlus}>
          <FlightCaption flight={flight} />
        </div>
      )}
    </div>
  );
}

export function LiftoffHero({ data, offset, stale }: { data: LiveData; offset: number; stale: boolean }) {
  const l = useLocale();
  const f = useFormat();
  const { roundNo, counters } = crashCounters(data, stale);
  return (
    <section className={s.hero}>
      <SceneBg game="crash" width={640} height={342} />
      <div className={s.cardHead}>
        <span className={s.title16}>Liftoff</span>
        <NewMark game="crash" />
        <span className={s.monoDim}>{roundNo}</span>
        <span className={s.spacer} />
        <span className={`${s.monoDim} ${s.teal}`}>● {counters ? f.int(counters.players) : DASH}</span>
      </div>
      <LiftoffChart data={data} offset={offset} />
      <div className={s.heroFoot}>
        <span>
          <FormattedMessage id="lobby.bets" /> <b>{counters ? f.int(counters.bets) : DASH}</b>
        </span>
        <span>
          <FormattedMessage id="lobby.cashed" />{' '}
          <b>{counters ? `${f.int(counters.cashed)}/${f.int(counters.players)}` : DASH}</b>
        </span>
        <span className={s.spacer} />
        <Link to={paths.game(l, 'crash')} className={s.play}>
          <FormattedMessage id="lobby.play" />
        </Link>
      </div>
    </section>
  );
}

/* ---------------- Pot card ---------------- */
export function PotCard({ data, potLeft }: { data: LiveData; potLeft: number | null }) {
  const l = useLocale();
  const f = useFormat();
  const name = useGameName();
  return (
    <Link to={paths.game(l, 'pot')} className={s.pot}>
      <SceneBg game="pot" width={320} height={164} />
      <div
        className={s.donut}
        style={{ background: potGradient(data?.pot?.top.map((p) => p.stake) ?? [], data?.pot?.bank ?? 0) }}
        aria-hidden="true"
      >
        <div className={s.donutHole}>{potFill(data)}</div>
      </div>
      <div className={s.potInfo}>
        <div className={s.title16}>
          {name('pot')} <NewMark game="pot" />
        </div>
        <div className={s.potBank}>{data?.pot ? f.int(data.pot.bank) : DASH}</div>
        {potLeft !== null && (
          <div>
            <span
              className={cx(s.monoDim, s.countdown, s.onScene, s.onSceneStart)}
              style={{ textTransform: 'uppercase' }}
            >
              <FormattedMessage id="lobby.start" /> {formatCountdown(potLeft)}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

/* ---------------- Roulette card ---------------- */
export const CELL_BG: Record<'red' | 'black' | 'green', string> = {
  red: 'var(--red)',
  black: 'var(--roulette-black)',
  green: 'var(--teal)',
};

export function RouletteCard({ data, clock }: { data: LiveData; clock: RouletteClock }) {
  const l = useLocale();
  const f = useFormat();
  const intl = useIntl();
  const name = useGameName();
  const label = useRouletteLabel();
  const short = (c: 'red' | 'black' | 'green') => intl.formatMessage({ id: `lobby.roulette.short.${c}` });
  // last outcomes; missing cells (no data / fewer rounds so far) stay neutral grey
  const last = data?.roulette?.last ?? [];
  const cells = Array.from({ length: 9 }, (_, i) => last[i] ?? null);
  const share = rouletteShares(data);
  const pct = (c: 'red' | 'black' | 'green') => (share ? f.pct(share[c]) : DASH);
  return (
    <Link to={paths.game(l, 'roulette')} className={s.roul}>
      <SceneBg game="roulette" width={320} height={164} />
      <div className={s.roulHead}>
        <span className={s.title16}>{name('roulette')}</span>
        <NewMark game="roulette" />
        <span className={cx('mono', s.countdown, s.onScene)} style={{ fontSize: 11 }}>
          {label(clock)}
        </span>
      </div>
      <div className={s.strip} aria-hidden="true">
        <div className={s.marker} />
        {cells.map((c, i) => (
          <span
            key={i}
            className={s.stripCell}
            style={{ backgroundColor: c ? CELL_BG[c] : 'var(--border-2)' }}
          />
        ))}
      </div>
      <div className={s.shares} title={intl.formatMessage({ id: 'lobby.roulette.sharesHint' })}>
        <span style={{ color: 'var(--red-text)' }}>
          {short('red')} {pct('red')}
        </span>
        <span>
          {short('black')} {pct('black')}
        </span>
        <span style={{ color: 'var(--teal-text)' }}>
          {short('green')} {pct('green')}
        </span>
      </div>
    </Link>
  );
}

/* ---------------- Originals with a cover: solo games, Red Envelopes, Mahjong Tables, Dou Dizhu ---------------- */
/** «Table 18: 3 of 8 seated»; several tables of the stake — «Stake 18: 2 tables, 12 of 16 seated». */
function hbTableTitle(intl: ReturnType<typeof useIntl>, t: HbLiveTable): string {
  const values = { room: t.room, players: t.players, capacity: t.capacity, count: t.count };
  return intl.formatMessage({ id: t.count > 1 ? 'lobby.hongbao.tableMany' : 'lobby.hongbao.table' }, values);
}

/** Red Envelopes tables on the desktop card: «18 3/8 · Start 00:07» per stake, «18 2 tables 12/16» for several tables. */
function HbTableChips({ tables }: { tables: HbLiveTable[] }) {
  const intl = useIntl();
  return (
    <div className={s.hbTables}>
      {tables.map((t) => (
        <span key={t.room} className={s.hbTable} data-room={t.room} title={hbTableTitle(intl, t)}>
          <span className={s.hbRoom}>{t.room}</span>
          {t.count > 1 && (
            <span>
              <FormattedMessage id="lobby.hongbao.tables" values={{ count: t.count }} />
            </span>
          )}
          <b style={{ color: t.players > 0 ? 'var(--teal-text)' : undefined }}>
            {t.players}/{t.capacity}
          </b>
          {t.leftMs !== null && (
            <span style={{ color: 'var(--orange-text)', textTransform: 'uppercase' }}>
              <FormattedMessage id="lobby.start" /> {formatCountdown(t.leftMs)}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/** A shared-table queue: stake (Mahjong Tables) or tier base (Dou Dizhu), people waiting, tables playing. */
export type LiveQueue = Pick<MjtLiveQueue, 'queued' | 'tables'> & { stake: number };

/**
 * Mahjong Tables / Dou Dizhu queues on the desktop card: «18 3» per stake or tier base (people waiting), the title
 * says the tables playing too.
 */
function QueueChips({ queues, titleId }: { queues: readonly LiveQueue[]; titleId: string }) {
  const intl = useIntl();
  return (
    <div className={s.hbTables}>
      {queues.map((q) => (
        <span
          key={q.stake}
          className={s.hbTable}
          data-stake={q.stake}
          title={intl.formatMessage({ id: titleId }, { stake: q.stake, queued: q.queued, tables: q.tables })}
        >
          <span className={s.hbRoom}>{q.stake}</span>
          <b style={{ color: q.queued > 0 ? 'var(--teal-text)' : undefined }}>{q.queued}</b>
        </span>
      ))}
    </div>
  );
}

/** «Play», or «Coming soon» while the game's route is still the stub (app/soon.ts): the lobby never passes a stub off as playable. */
function PlayLabel({ game }: { game: GameId }) {
  return <FormattedMessage id={isPlayable(game) ? 'lobby.play' : 'common.soon.title'} />;
}

/** «card» funnel event of a skill game once its card is on screen (no IntersectionObserver → nothing) */
function useSkillCardSeen(game: GameId) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !isSkillGame(game) || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        sendSkillEvent({ e: 'card', g: game });
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [game]);
  return ref;
}

/** The «Play» of a card: the kit's button look on a span (the whole card is the link; no button inside a link). */
const PLAY_CLASS = cx(
  buttonClasses.btn,
  buttonClasses.teal,
  buttonClasses.solid,
  buttonClasses.sm,
  s.cardPlay,
);

const isSlot = (g: GameId): g is SlotId => (SLOT_IDS as readonly string[]).includes(g);

/** Cover size of a card (16:10): desktop tiles are up to ~300 px wide, phone tiles ~175 px. */
const CARD_COVER = { desktop: { w: 320, h: 200 }, phone: { w: 180, h: 112 } } as const;

/** «● 14 playing» on a slot card: distinct players in the last minutes (the title says how many minutes). */
function SlotOnline({ data, game }: { data: LiveData; game: SlotId }) {
  const intl = useIntl();
  const f = useFormat();
  const n = data?.slots[game];
  if (n === undefined || !data) return <span className={s.cardLive}>● {DASH}</span>;
  return (
    <span
      className={s.cardLive}
      title={intl.formatMessage({ id: 'lobby.slotPlayers' }, { count: n, min: data.activeWindowMin })}
    >
      ● <FormattedMessage id="lobby.playing" values={{ count: n, n: f.int(n) }} />
    </span>
  );
}

/**
 * Game card of the lobby grid (desktop and phone): cover with the mechanic as a tag, name, short size line, the lead
 * (desktop originals), live data — seats per table (Red Envelopes), people queued (Mahjong Tables, Dou Dizhu), players
 * of a slot — and «Play». The whole card is one link.
 */
export function GameCard({
  game,
  data = null,
  tables = null,
  queues = null,
  compact = false,
}: {
  game: CoverOriginal | SlotId | SkillGameId;
  data?: LiveData;
  tables?: HbLiveTable[] | null;
  queues?: readonly LiveQueue[] | null;
  /** phone: smaller cover, no lead, the mechanic under the cover (never text over the picture) */
  compact?: boolean;
}) {
  const l = useLocale();
  const name = useGameName();
  const seen = useSkillCardSeen(game);
  const cover = compact ? CARD_COVER.phone : CARD_COVER.desktop;
  const slot = isSlot(game);
  const text = slot ? null : ORIGINAL_TEXT[game];
  const queueTitle = slot ? '' : (QUEUE_TITLE[game as CoverOriginal] ?? 'lobby.mahjongTable.queue');
  const playable = isPlayable(game);
  const tag = (
    <span className={cx(s.cardTag, compact && s.cardTagBelow)}>
      <FormattedMessage id={MECH[game]} />
    </span>
  );
  const inner = (
    <>
      <div className={s.cardCover}>
        <GameCover game={game} width={cover.w} height={cover.h} lazy />
        {playable && <NewMark game={game} variant="card" />}
        {!compact && tag}
      </div>
      <div className={s.cardBody}>
        <span className={s.cardName}>{name(game)}</span>
        {compact && tag}
        {text && (
          <span className={s.cardSize}>
            <FormattedMessage id={text.size} values={text.values} />
          </span>
        )}
        {text && !compact && (
          <p className={s.cardLead}>
            <FormattedMessage id={text.lead} values={text.values} />
          </p>
        )}
        {slot && <SlotOnline data={data} game={game} />}
        {tables && <HbTableChips tables={tables} />}
        {queues && <QueueChips queues={queues} titleId={queueTitle} />}
        <span className={cx(PLAY_CLASS, !playable && s.cardSoon)}>
          {slot ? <FormattedMessage id="lobby.play" /> : <PlayLabel game={game} />}
        </span>
      </div>
    </>
  );
  // a game behind «coming soon»: the card is shown, never a link into an empty screen
  if (!playable)
    return (
      <div
        ref={(el) => void (seen.current = el)}
        className={cx(s.gameCard, s.gameCardSoon)}
        data-game={game}
        aria-disabled="true"
      >
        {inner}
      </div>
    );
  return (
    <Link
      ref={(el) => void (seen.current = el)}
      to={slot ? paths.slot(l, game) : paths.game(l, game)}
      className={s.gameCard}
      data-game={game}
    >
      {inner}
    </Link>
  );
}

/**
 * The reel sections carry their raster icons as a pair (`slots` / `machines`, 24 px): both or none, so «Slots» and
 * «Machines» never differ; without the raster the titles stay text.
 */
const SECTION_ICONS = ['slots', 'machines'] as const;
const SECTION_ICON_PX = 24;

/** A titled section of the lobby grid («Originals», «Tables», «Slots»). */
export function GameSection({
  titleId,
  children,
  lead,
  icon,
}: {
  titleId: string;
  children: ReactNode;
  lead?: ReactNode;
  /** the section's raster icon before the title (decorative) */
  icon?: (typeof SECTION_ICONS)[number];
}) {
  const intl = useIntl();
  const title = intl.formatMessage({ id: titleId });
  return (
    <section className={s.section} aria-label={title}>
      <h2 className={s.sectionTitle}>
        {icon && (
          <UiIcon
            name={icon}
            size={SECTION_ICON_PX}
            together={SECTION_ICONS}
            fallback={null}
            className={s.sectionIco}
          />
        )}
        {title}
      </h2>
      {lead}
      <div className={s.cardGrid}>{children}</div>
    </section>
  );
}

/* ---------------- Right rail ---------------- */
interface PotRow {
  nickname: string;
  stake: number;
  /** the house bot (a playful name seeded by the round, «· bot») */
  bot?: true;
  chance: number;
  color: string;
}

export function PotBankTable({ data }: { data: LiveData }) {
  const f = useFormat();
  const nick = useNickname();
  const botName = useHouseBotName();
  const pot = data?.pot ?? null;
  const rows: PotRow[] = (pot?.top ?? []).map((p, i) => ({
    ...p,
    chance: pot?.bank ? p.stake / pot.bank : 0,
    color: POT_COLORS[i % POT_COLORS.length] ?? 'var(--muted)',
  }));
  const more = Math.max(0, (pot?.players ?? 0) - rows.length);
  const cols: Column<PotRow>[] = [
    {
      key: 'p',
      header: <FormattedMessage id="lobby.player" />,
      render: (r) => (
        <span className={s.player}>
          <span className={s.swatch} style={{ backgroundColor: r.color }} />
          {r.bot ? botName(potBotSeed(pot?.roundNo ?? 0)) : nick(r.nickname)}
        </span>
      ),
    },
    {
      key: 's',
      header: <FormattedMessage id="lobby.stake" />,
      width: '70px',
      align: 'right',
      mono: true,
      render: (r) => f.int(r.stake),
    },
    {
      key: 'c',
      header: <FormattedMessage id="lobby.chance" />,
      width: '76px',
      align: 'right',
      mono: true,
      render: (r) => f.pct(r.chance),
      cellStyle: () => ({ color: 'var(--muted)' }),
    },
  ];
  return (
    <>
      <div className={s.railHead}>
        <span className={s.railTitle}>
          <FormattedMessage id="lobby.potBank" />
        </span>
        <span className={s.railBank}>{pot ? f.int(pot.bank) : DASH}</span>
      </div>
      <Table columns={cols} rows={rows} rowKey={(r) => r.nickname} />
      {rows.length === 0 && (
        <div className={s.more}>{pot ? <FormattedMessage id="lobby.potEmpty" /> : DASH}</div>
      )}
      {more > 0 && (
        <div className={s.more}>
          <FormattedMessage id="lobby.more" values={{ count: more }} />
        </div>
      )}
    </>
  );
}

export function LiftoffFeed({ data, limit = 12 }: { data: LiveData; limit?: number }) {
  const f = useFormat();
  return (
    <>
      <div className={s.feedHead}>
        <span>
          <FormattedMessage id="lobby.feed" />
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--dim)' }}>
          #
        </span>
      </div>
      <div>
        {!data?.crash?.history.length && (
          <div className={s.more}>{data?.crash ? <FormattedMessage id="lobby.feedEmpty" /> : DASH}</div>
        )}
        {(data?.crash?.history ?? []).slice(0, limit).map((h) => {
          const r = { roundNo: h.roundNo, multiplier: h.crashPoint };
          const c = multiplierColor(r.multiplier);
          const w = Math.min(100, (Math.log(r.multiplier) / Math.log(25)) * 100 + 4);
          return (
            <div key={r.roundNo} className={s.feedRow}>
              <span style={{ color: 'var(--dim)' }}>#{r.roundNo}</span>
              <span className={s.feedBar} aria-hidden="true">
                <span style={{ width: `${w.toFixed(0)}%`, background: c }} />
              </span>
              <span className={s.feedVal} style={{ color: c }}>
                {f.mult(r.multiplier)}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}
